import { tokens } from '@fluentui/react-components';

/**
 * The named foundation scales (#202).
 *
 * One module that gives every spacing, radius and elevation decision a name, so
 * a developer picks a step by INTENT rather than by guessing a pixel value.
 * Purely declarative re-exports of Fluent token references — no logic, nothing
 * decision-carrying, which is why this file lives outside the coverage scope.
 *
 * Before this existed, `apps/web/src` contained 196 raw pixel string literals
 * ('8px' ×57, '16px' ×49, '12px' ×31, '4px' ×21, '24px' ×8, '1px' ×4, '20px' ×3)
 * and ZERO uses of `spacingHorizontal*`/`spacingVertical*`/`strokeWidthThin`.
 * Every one of those values maps 1:1 onto a token, so the sweep is lossless.
 *
 * ── Why not just use `tokens.spacingVerticalL` directly? ──
 * You may. `SPACE.l` IS `tokens.spacingVerticalL`. The names exist so the table
 * below — what each step is FOR — has somewhere to live, and so that the three
 * banned stops are impossible to reach by autocomplete.
 */

/**
 * BANNED: three of Fluent's eleven spacing stops are off the 4px grid the
 * project mandates, and using a Fluent token is NOT automatically compliance.
 *
 *   spacing*XXS    = 2px
 *   spacing*SNudge = 6px
 *   spacing*MNudge = 10px
 *
 * They are deliberately absent from `SPACE`. The two places they leaked in
 * before this pass were `gap: '2px'` and `padding: '0 6px'`.
 */

/**
 * Vertical spacing. THE DEFAULT IS `l` (16px).
 *
 * | step   | px | use it for — and only for                                                        |
 * | ------ | -- | -------------------------------------------------------------------------------- |
 * | `none` |  0 | resetting a user-agent default (`ul`/`li` padding, `fieldset`)                    |
 * | `xs`   |  4 | icon ↔ text; a badge dot ↔ its word; a chip's inner vertical pad                  |
 * | `s`    |  8 | label ↔ its input; tile label ↔ its number; buttons within one action cluster     |
 * | `m`    | 12 | stacked form fields inside one card; toolbar control gaps                        |
 * | `l`    | 16 | card padding; heading ↔ content; gap between sibling cards and stacked blocks     |
 * | `xxl`  | 24 | desktop page gutter (≥700px), and the page header's bottom gap                    |
 * | `xxxl` | 32 | `EmptyState` internal block padding only                                         |
 *
 * `spacing*XL` (20px) is deliberately omitted: it is on the grid but has no
 * assigned role, and an unowned step is how a second, competing rhythm starts.
 */
export const SPACE = {
  none: tokens.spacingVerticalNone,
  xs: tokens.spacingVerticalXS,
  s: tokens.spacingVerticalS,
  m: tokens.spacingVerticalM,
  l: tokens.spacingVerticalL,
  xxl: tokens.spacingVerticalXXL,
  xxxl: tokens.spacingVerticalXXXL,
} as const;

/** Horizontal spacing. Same scale, same meanings — Fluent keeps the two axes separate. */
export const SPACE_X = {
  none: tokens.spacingHorizontalNone,
  xs: tokens.spacingHorizontalXS,
  s: tokens.spacingHorizontalS,
  m: tokens.spacingHorizontalM,
  l: tokens.spacingHorizontalL,
  xxl: tokens.spacingHorizontalXXL,
  xxxl: tokens.spacingHorizontalXXXL,
} as const;

/**
 * Corner radius — four stops.
 *
 * ⚠️ THE FLUENT 2 DESIGN SITE IS WRONG FOR THIS VERSION. fluent2.microsoft.design
 * states Large = 8px and X-Large = 12px. The installed `@fluentui/tokens` at
 * 9.74.4 resolves Large to **6px** and XLarge to **8px** (verified by reading
 * the theme object at runtime, not the docs). Where they disagree the installed
 * package wins — and this exact confusion is why the app previously had ten uses
 * of `borderRadiusLarge` rendering 6px while the approved card radius was 8px.
 *
 * | name      | token                     | px | use it for                                        |
 * | --------- | ------------------------- | -- | ------------------------------------------------- |
 * | `none`    | `borderRadiusNone`        |  0 | the full-screen mobile sheet dialog only          |
 * | `control` | `borderRadiusMedium`      |  4 | Fluent's own default for every interactive control — do not override it; AND any panel nested INSIDE a card, because an 8px corner inside an 8px corner reads wrong |
 * | `surface` | `borderRadiusXLarge`      |  8 | THE CARD RADIUS. Cards, stat tiles, sections, table containers, dropzones, empty-state frames, desktop dialog surfaces |
 * | `pill`    | `borderRadiusCircular`    |  ∞ | a status dot, a wizard step number                |
 *
 * NEVER redefine these in the theme object. `borderRadius*` tokens are GLOBAL
 * and byte-identical across all three shipped themes, so overriding
 * `borderRadiusXLarge` would change Button, Input and Menu app-wide rather than
 * just cards. Reference the token from `makeStyles`; never write '8px'.
 */
export const RADIUS = {
  none: tokens.borderRadiusNone,
  control: tokens.borderRadiusMedium,
  surface: tokens.borderRadiusXLarge,
  pill: tokens.borderRadiusCircular,
} as const;

/**
 * Elevation — five levels, taken from what Fluent's own components actually
 * resolve to rather than from the design site, which contradicts them.
 *
 * | level | shadow    | means                                                                  |
 * | ----- | --------- | ---------------------------------------------------------------------- |
 * | E0    | (none)    | STRUCTURE, not an object: table rows, panels inset inside a card, field groups. Border only. |
 * | E1    | `shadow4` | A RESTING SURFACE — every card, stat tile, section, table container.    |
 * | E2    | `shadow8` | RAISED: a clickable card on hover/focus-within, and a sticky action bar floating above content scrolling under it. |
 * | E3    | `shadow16`| Transient overlay — popover, menu, combobox listbox, calendar. **Fluent applies this itself; never set it.** |
 * | E4    | `shadow64`| Modal — dialog surface, drawer. **Fluent applies this itself; never set it.** |
 *
 * BANNED: `shadow2` (Fluent's pre-appearance base and its DISABLED state — a
 * resting card at shadow2 reads a step flatter than every other Fluent surface
 * in the same tab), `shadow28` (used by no component in this dependency tree),
 * and every `shadow*Brand` variant (reserved, not general elevation).
 *
 * TWO RULES THAT DO NOT BEND:
 *  1. Never write a `box-shadow` literal. The ambient and key colours resolve
 *     from `colorNeutralShadowAmbient`/`Key` per theme; a hand-written `rgba()`
 *     would be wrong in dark, meaningless in high contrast, and breaks the
 *     no-`rgb()` hard rule.
 *  2. Every elevated surface carries the E0 border IN ADDITION to its shadow.
 *     Shadows are effectively invisible under `teamsHighContrastTheme`, where
 *     the background collapses to pure black — the border is what survives.
 */
export const ELEVATION = {
  resting: tokens.shadow4,
  raised: tokens.shadow8,
} as const;

/**
 * Borders. `strokeWidthThin` for every surface edge and row divider — this is
 * what Fluent's own `TableRow` uses. `strokeWidthThick` (2px) is reserved for
 * the selected/active indicator, so "this one is chosen" is legible as a
 * WIDTH change and therefore survives high contrast, where the brand hue is
 * discarded entirely.
 */
export const BORDER = {
  hairline: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  /** A stronger boundary: the table header's underside, an action bar's top edge. */
  divider: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
  /** Selected or active. Always paired with a second, non-colour signal. */
  selected: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}`,
} as const;

/**
 * Layout widths — the answer to "fill the Teams canvas without looking stretched".
 *
 * A Teams tab is not a centred marketing page: a table must fill the canvas at
 * every viewport (#189). But a text input stretched to 1800px is unreadable, so
 * "use the space" cannot mean "widen everything".
 *
 * THE RULE: wide space is filled by ADDING A COLUMN, never by widening one.
 *
 *  - FLUID surfaces (tables, queues, audit log, stat-tile rows) take the full
 *    canvas width and are capped by nothing.
 *  - MEASURED surfaces (form fields, body copy, empty-state text, dialog bodies)
 *    cap at `MEASURE`, because reading and scanning both degrade past roughly
 *    75 characters per line.
 *
 * A companion-rail width used to live here for the New Request wizard's
 * summary panel and the reviewer's decision panel — both replaced by a
 * full-width `fluid` column plus a full-width bottom `ActionBar` (#206, #209).
 */
export const LAYOUT = {
  /** Max line length for a measured column — ~75ch at the 14px body size. */
  measure: '720px',
  /**
   * The stat-tile row's cap: four 240px tiles plus their three 16px gaps.
   *
   * Counters are a compact summary, not data that grows with the canvas —
   * uncapped they measured 332px each on a 1440px tab for a two-digit number,
   * which is the "stretched" failure the layout rule exists to prevent. Fluid
   * is for tables; this is not one.
   */
  tileRowMax: '1008px',
  /**
   * A table body cell's wrap point (#211). Long content — a vendor name, a
   * request title, a description — caps here and wraps onto a new line
   * instead of growing its column and forcing the whole table into
   * horizontal scroll. Short/numeric cells (a date, a status word, a PR
   * number) never reach 320px, so the cap is invisible to them; it only ever
   * engages the columns that were actually cramped in #211's screenshot.
   * Header cells are deliberately excluded — `DataTable`'s `headerCell`
   * stays `whiteSpace: 'nowrap'` on purpose, so a wrapping header does not
   * re-flow the whole column underneath it.
   */
  cellWrap: '320px',
} as const;
